import { useState } from 'preact/hooks';

// Choices remembered on this device only (view links, folds): small conveniences, never
// data. The browser may refuse storage (private windows, previews), so every read and write
// is guarded and the page works without it.

const PREFIX = 'rl:';

export function recall<T>(key: string, fallback: T): T {
  try {
    const v = localStorage.getItem(PREFIX + key);
    return v === null ? fallback : (JSON.parse(v) as T);
  } catch {
    return fallback;
  }
}

export function keep<T>(key: string, value: T): void {
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    /* storage refused: the choice lasts until the page closes */
  }
}

/** Like useState, remembered on this device under `key`. */
export function useRemembered<T>(key: string, fallback: T): [T, (v: T) => void] {
  const [s, setS] = useState<{ key: string; v: T }>(() => ({ key, v: recall(key, fallback) }));
  // A new key (Reference's subgenre picks follow the genre you pick) reads that key's choice.
  const v = s.key === key ? s.v : recall(key, fallback);
  if (s.key !== key) setS({ key, v });
  return [
    v,
    (next: T) => {
      setS({ key, v: next });
      keep(key, next);
    },
  ];
}
